<template>
  <div class="login-view">
    <!-- Atmosphärischer Hintergrund -->
    <div class="login-bg" aria-hidden="true">
      <div class="login-bg__mesh"></div>
      <div class="login-bg__orb login-bg__orb--1"></div>
      <div class="login-bg__orb login-bg__orb--2"></div>
      <div class="login-bg__orb login-bg__orb--3"></div>
      <div class="login-bg__grid"></div>
    </div>

    <div class="login-shell">
      <!-- Branding (Desktop links) -->
      <aside class="login-brand">
        <div class="login-brand__inner">
          <div class="login-brand__icon">
            <v-icon size="32" color="success">mdi-wrench-cog</v-icon>
          </div>
          <h1 class="login-brand__title">ARCM</h1>
          <p class="login-brand__tagline">
            Administratives Reparatur- &amp; Commissions-Management
          </p>
          <ul class="login-brand__features">
            <li><v-icon size="16" color="success">mdi-check-circle-outline</v-icon> Reparatur &amp; Retouren</li>
            <li><v-icon size="16" color="success">mdi-check-circle-outline</v-icon> Commission &amp; Versand</li>
            <li><v-icon size="16" color="success">mdi-check-circle-outline</v-icon> Inventur &amp; DTC</li>
          </ul>
        </div>
      </aside>

      <!-- Login-Formular -->
      <main class="login-main">
        <div class="login-card">
          <header class="login-card__header">
            <div class="login-card__mobile-brand">
              <v-icon size="28" color="success">mdi-wrench-cog</v-icon>
              <span>ARCM</span>
            </div>
            <h2 class="login-card__title">Willkommen zurück</h2>
            <p class="login-card__subtitle">Melde dich an, um fortzufahren</p>
          </header>

          <transition name="fade" mode="out-in">
            <v-alert
              v-if="accountLocked"
              type="error"
              variant="tonal"
              density="compact"
              class="login-alert"
              :text="`Konto gesperrt. Bitte warten Sie ${lockTimeMinutes} Minuten.`"
            >
              <template #prepend>
                <v-icon>mdi-lock</v-icon>
              </template>
            </v-alert>

            <v-alert
              v-else-if="remainingAttempts !== null"
              type="warning"
              variant="tonal"
              density="compact"
              class="login-alert"
              :text="`Verbleibende Versuche: ${remainingAttempts} von ${maxAttempts}`"
            >
              <template #prepend>
                <v-icon>mdi-alert</v-icon>
              </template>
            </v-alert>
          </transition>

          <v-form class="login-form" @submit.prevent="login">
            <v-text-field
              v-model="loginname"
              label="Benutzername"
              prepend-inner-icon="mdi-account-outline"
              variant="outlined"
              color="success"
              autocomplete="username"
              :disabled="isLoading || accountLocked"
              class="login-field"
              hide-details="auto"
            />

            <v-text-field
              v-model="passwort"
              label="Passwort"
              prepend-inner-icon="mdi-lock-outline"
              :append-inner-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
              :type="showPassword ? 'text' : 'password'"
              variant="outlined"
              color="success"
              autocomplete="current-password"
              :disabled="isLoading || accountLocked"
              class="login-field"
              hide-details="auto"
              @click:append-inner="showPassword = !showPassword"
            />

            <div class="login-form__options">
              <v-checkbox
                v-model="rememberMe"
                color="success"
                hide-details
                :disabled="isLoading || accountLocked"
                density="compact"
              >
                <template #label>
                  <span class="login-form__remember">
                    Angemeldet bleiben
                    <v-tooltip location="top">
                      <template #activator="{ props }">
                        <v-icon
                          v-bind="props"
                          size="14"
                          class="login-form__info"
                        >
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <div class="login-tooltip">
                        <div>Benutzername wird gespeichert</div>
                        <div>Session bleibt 7 Tage gültig (statt 24 h)</div>
                      </div>
                    </v-tooltip>
                  </span>
                </template>
              </v-checkbox>

              <button
                type="button"
                class="login-form__forgot"
                :disabled="isLoading || accountLocked"
                @click="showForgotPasswordDialog"
              >
                Passwort vergessen?
              </button>
            </div>

            <v-btn
              block
              color="success"
              size="large"
              type="submit"
              :loading="isLoading"
              :disabled="isLoading || accountLocked"
              class="login-submit"
              rounded="lg"
            >
              <v-icon v-if="!isLoading && !accountLocked" start>mdi-login</v-icon>
              {{ accountLocked ? 'Gesperrt' : 'Anmelden' }}
            </v-btn>
          </v-form>

          <footer class="login-card__footer">
            <span>© {{ currentYear }} ARCM · alphadystopie.de · devhub-it.de</span>
          </footer>
        </div>
      </main>
    </div>

    <!-- Passwort-Reset Dialog -->
    <v-dialog v-model="forgotPasswordDialog" max-width="440" class="login-dialog">
      <v-card class="login-dialog__card" rounded="xl">
        <v-card-title class="login-dialog__title">
          <div class="login-dialog__title-icon">
            <v-icon color="success" size="22">mdi-lock-reset</v-icon>
          </div>
          Passwort zurücksetzen
        </v-card-title>

        <v-card-text class="login-dialog__body">
          <p class="login-dialog__text">
            Gib deine E-Mail-Adresse ein. Du erhältst einen Link zum Zurücksetzen deines Passworts.
          </p>

          <v-form @submit.prevent="requestPasswordReset">
            <v-text-field
              v-model="resetEmail"
              label="E-Mail-Adresse"
              prepend-inner-icon="mdi-email-outline"
              type="email"
              variant="outlined"
              color="success"
              required
              class="login-field"
              hide-details="auto"
            />
          </v-form>
        </v-card-text>

        <v-card-actions class="login-dialog__actions">
          <v-btn variant="text" @click="forgotPasswordDialog = false">
            Abbrechen
          </v-btn>
          <v-spacer />
          <v-btn
            color="success"
            variant="flat"
            rounded="lg"
            :loading="resetting"
            :disabled="resetting"
            @click="requestPasswordReset"
          >
            Link senden
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick, computed } from 'vue';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useAuthStore } from '@/stores/auth';
import { useToast } from 'vue-toastification';

const toast = useToast();
const authStore = useAuthStore();

const loginname = ref('');
const passwort = ref('');
const rememberMe = ref(false);
const showPassword = ref(false);
const isLoading = ref(false);
const forgotPasswordDialog = ref(false);
const resetEmail = ref('');
const resetting = ref(false);
const accountLocked = ref(false);
const remainingAttempts = ref(null);
const lockTimeMinutes = ref(0);
const maxAttempts = ref(5);

const currentYear = computed(() => new Date().getFullYear());

const login = async () => {
  if (!loginname.value || !passwort.value) {
    toast.error('Bitte Benutzername und Passwort eingeben');
    return;
  }

  try {
    isLoading.value = true;

    const response = await axios.post(
      `${API_BASE_URL}login.php`,
      {
        action: 'login',
        loginname: loginname.value,
        passwort: passwort.value,
        rememberMe: rememberMe.value,
      },
      { withCredentials: true }
    );

    if (response.data.status === 'error') {
      const errorData = response.data;

      if (errorData.locked) {
        accountLocked.value = true;
        lockTimeMinutes.value = errorData.remainingMinutes || 0;
        toast.error(
          `Konto gesperrt. Bitte warten Sie ${lockTimeMinutes.value} Minuten.`
        );
      } else if (errorData.attemptData) {
        remainingAttempts.value = errorData.attemptData.attemptsLeft;
        maxAttempts.value = errorData.attemptData.maxAttempts;
        toast.error(errorData.message);
      } else {
        toast.error(errorData.message || 'Anmeldung fehlgeschlagen');
      }
      return;
    }

    accountLocked.value = false;
    remainingAttempts.value = null;

    if (response.data.rememberUsername) {
      localStorage.setItem('lastUsername', response.data.rememberUsername);
    }

    authStore.user = response.data.user;
    authStore.isAuthenticated = true;
    localStorage.setItem('userData', JSON.stringify(authStore.user));

    authStore.setupActivityTracking();

    toast.success('Login erfolgreich!');

    await nextTick();

    setTimeout(() => {
      if (
        authStore.user?.admin ||
        authStore.user?.monteur ||
        authStore.user?.returen_sorting ||
        authStore.user?.lagerist ||
        authStore.user?.super_admin ||
        authStore.user?.developer ||
        authStore.user?.refurbish ||
        authStore.user?.office ||
        authStore.user?.dtc
      ) {
        if (
          authStore.user?.dtc &&
          !authStore.user?.admin &&
          !authStore.user?.super_admin &&
          !authStore.user?.developer &&
          !authStore.user?.monteur &&
          !authStore.user?.returen_sorting &&
          !authStore.user?.lagerist &&
          !authStore.user?.refurbish &&
          !authStore.user?.office
        ) {
          window.location.href = '/dtc';
        } else if (
          authStore.user?.refurbish &&
          !authStore.user?.admin &&
          !authStore.user?.super_admin &&
          !authStore.user?.developer &&
          !authStore.user?.monteur &&
          !authStore.user?.returen_sorting &&
          !authStore.user?.lagerist
        ) {
          window.location.href = '/refurbish';
        } else if (
          authStore.user?.lagerist &&
          !authStore.user?.admin &&
          !authStore.user?.super_admin &&
          !authStore.user?.developer &&
          !authStore.user?.monteur &&
          !authStore.user?.returen_sorting
        ) {
          window.location.href = '/commission';
        } else {
          window.location.href = '/dashboard';
        }
      } else {
        window.location.href = '/empty';
      }
    }, 100);
  } catch (error) {
    console.error('Login fehlgeschlagen:', error);
    toast.error(
      'Ein Fehler ist bei der Kommunikation mit dem Server aufgetreten.'
    );
  } finally {
    isLoading.value = false;
  }
};

const requestPasswordReset = async () => {
  if (!resetEmail.value) {
    toast.error('Bitte E-Mail-Adresse eingeben');
    return;
  }

  try {
    resetting.value = true;

    const response = await axios.post(
      `${API_BASE_URL}forgot_password.php`,
      { action: 'reset', email: resetEmail.value },
      { headers: { 'Content-Type': 'application/json' } }
    );

    if (response.data.status === 'success') {
      toast.success(
        'Passwort-Reset-Link wurde an Ihre E-Mail-Adresse gesendet!'
      );
      resetEmail.value = '';
      forgotPasswordDialog.value = false;
    } else {
      toast.error(
        response.data.message || 'Fehler beim Senden des Reset-Links'
      );
    }
  } catch (error) {
    console.error('Fehler beim Zurücksetzen des Passworts:', error);
    const serverMessage = error?.response?.data?.message;
    toast.error(
      serverMessage ||
        'Fehler beim Senden des Reset-Links. Bitte versuchen Sie es später erneut.'
    );
  } finally {
    resetting.value = false;
  }
};

const showForgotPasswordDialog = () => {
  forgotPasswordDialog.value = true;
};

onMounted(() => {
  const lastUsername = localStorage.getItem('lastUsername');
  if (lastUsername) {
    loginname.value = lastUsername;
    nextTick(() => {
      const passwordField = document.querySelector('input[type="password"]');
      if (passwordField) {
        passwordField.focus();
      }
    });
  }
});
</script>

<style scoped>
/* ─── Design Tokens ─── */
.login-view {
  --login-accent: #4caf50;
  --login-accent-glow: rgba(76, 175, 80, 0.35);
  --login-surface: rgba(18, 20, 28, 0.72);
  --login-surface-border: rgba(255, 255, 255, 0.08);
  --login-text: rgba(255, 255, 255, 0.95);
  --login-text-muted: rgba(255, 255, 255, 0.55);
  --login-radius: 20px;

  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #0a0c10;
}

/* ─── Hintergrund ─── */
.login-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
}

.login-bg__mesh {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 80% 60% at 20% 40%, rgba(76, 175, 80, 0.08) 0%, transparent 60%),
    radial-gradient(ellipse 60% 50% at 80% 70%, rgba(56, 142, 60, 0.06) 0%, transparent 55%),
    linear-gradient(160deg, #0a0c10 0%, #0f1219 40%, #0a0e14 100%);
}

.login-bg__orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.5;
  animation: orb-float 18s ease-in-out infinite;
}

.login-bg__orb--1 {
  width: 420px;
  height: 420px;
  top: -8%;
  left: -6%;
  background: rgba(76, 175, 80, 0.12);
}

.login-bg__orb--2 {
  width: 320px;
  height: 320px;
  bottom: 10%;
  right: -4%;
  background: rgba(38, 166, 154, 0.1);
  animation-delay: -6s;
}

.login-bg__orb--3 {
  width: 200px;
  height: 200px;
  top: 50%;
  left: 45%;
  background: rgba(76, 175, 80, 0.06);
  animation-delay: -12s;
}

.login-bg__grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.025) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(ellipse 70% 70% at 50% 50%, black 20%, transparent 75%);
}

@keyframes orb-float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(24px, -16px) scale(1.04); }
  66% { transform: translate(-16px, 12px) scale(0.96); }
}

/* ─── Layout Shell ─── */
.login-shell {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  max-width: 960px;
  min-height: 560px;
  margin: 24px;
  border-radius: calc(var(--login-radius) + 4px);
  border: 1px solid var(--login-surface-border);
  background: var(--login-surface);
  backdrop-filter: blur(24px) saturate(1.4);
  -webkit-backdrop-filter: blur(24px) saturate(1.4);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.04) inset,
    0 24px 80px rgba(0, 0, 0, 0.45),
    0 0 60px rgba(76, 175, 80, 0.04);
  overflow: hidden;
}

/* ─── Branding (links) ─── */
.login-brand {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 40px;
  border-right: 1px solid var(--login-surface-border);
  background: linear-gradient(
    145deg,
    rgba(76, 175, 80, 0.06) 0%,
    transparent 60%
  );
}

.login-brand__inner {
  max-width: 320px;
}

.login-brand__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: rgba(76, 175, 80, 0.12);
  border: 1px solid rgba(76, 175, 80, 0.25);
  margin-bottom: 24px;
  box-shadow: 0 0 24px var(--login-accent-glow);
}

.login-brand__title {
  font-size: 2.75rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--login-text);
  margin: 0 0 12px;
  line-height: 1;
}

.login-brand__tagline {
  font-size: 0.95rem;
  line-height: 1.55;
  color: var(--login-text-muted);
  margin: 0 0 32px;
}

.login-brand__features {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.login-brand__features li {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
}

/* ─── Formular (rechts) ─── */
.login-main {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 40px;
}

.login-card {
  width: 100%;
  max-width: 360px;
}

.login-card__header {
  margin-bottom: 28px;
}

.login-card__mobile-brand {
  display: none;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--login-text);
  letter-spacing: -0.02em;
}

.login-card__title {
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: var(--login-text);
  margin: 0 0 6px;
}

.login-card__subtitle {
  font-size: 0.9rem;
  color: var(--login-text-muted);
  margin: 0;
}

.login-alert {
  margin-bottom: 20px;
  border-radius: 12px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* ─── Formular-Felder ─── */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.login-field :deep(.v-field) {
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  transition: background 0.2s ease, box-shadow 0.2s ease;
}

.login-field :deep(.v-field:hover) {
  background: rgba(255, 255, 255, 0.06);
}

.login-field :deep(.v-field--focused) {
  background: rgba(76, 175, 80, 0.06);
  box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.12);
}

.login-field :deep(.v-field__outline) {
  --v-field-border-opacity: 0.15;
}

.login-field :deep(.v-field--focused .v-field__outline) {
  --v-field-border-opacity: 0.6;
}

.login-form__options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin: 8px 0 20px;
}

.login-form__remember {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.85rem;
  color: var(--login-text-muted);
}

.login-form__info {
  color: rgba(255, 255, 255, 0.35);
  cursor: help;
  transition: color 0.2s;
}

.login-form__info:hover {
  color: var(--login-accent);
}

.login-tooltip {
  font-size: 0.8rem;
  line-height: 1.5;
}

.login-form__forgot {
  background: none;
  border: none;
  padding: 4px 0;
  font-size: 0.85rem;
  color: var(--login-accent);
  cursor: pointer;
  transition: opacity 0.2s;
}

.login-form__forgot:hover:not(:disabled) {
  opacity: 0.8;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.login-form__forgot:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.login-submit {
  height: 48px !important;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-transform: none;
  box-shadow: 0 4px 20px var(--login-accent-glow);
  transition: box-shadow 0.2s ease, transform 0.15s ease;
}

.login-submit:not(:disabled):hover {
  box-shadow: 0 6px 28px rgba(76, 175, 80, 0.45);
  transform: translateY(-1px);
}

.login-card__footer {
  margin-top: 32px;
  text-align: center;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.25);
}

/* ─── Dialog ─── */
.login-dialog__card {
  background: rgba(18, 20, 28, 0.95) !important;
  border: 1px solid var(--login-surface-border);
  backdrop-filter: blur(20px);
}

.login-dialog__title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.15rem;
  font-weight: 600;
  padding: 24px 24px 8px;
}

.login-dialog__title-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(76, 175, 80, 0.12);
}

.login-dialog__body {
  padding: 8px 24px 0;
}

.login-dialog__text {
  font-size: 0.9rem;
  line-height: 1.55;
  color: var(--login-text-muted);
  margin-bottom: 20px;
}

.login-dialog__actions {
  padding: 16px 24px 24px;
}

/* ─── Responsive ─── */
@media (max-width: 768px) {
  .login-shell {
    grid-template-columns: 1fr;
    max-width: 420px;
    min-height: auto;
  }

  .login-brand {
    display: none;
  }

  .login-main {
    padding: 36px 28px;
  }

  .login-card__mobile-brand {
    display: flex;
  }
}

@media (max-width: 480px) {
  .login-shell {
    margin: 12px;
    border-radius: var(--login-radius);
  }

  .login-main {
    padding: 28px 20px;
  }

  .login-form__options {
    flex-direction: column;
    align-items: flex-start;
  }

  .login-form__forgot {
    align-self: flex-end;
  }
}
</style>
